// Thumbnail generation utilities for the Drive feature
import { pdfjs } from 'react-pdf';

// Ensure pdfjs worker is configured — set here defensively so this module
// works even if MyDrive.tsx hasn't evaluated yet (e.g. lazy-loaded routes).
if (!pdfjs.GlobalWorkerOptions.workerSrc) {
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url
  ).toString();
}

const uploadThumbBlob = async (fileId: string, blob: Blob) => {
  const thumbUrl = `/api/uploads/thumb/upload?fileId=${encodeURIComponent(fileId)}`;
  const resp = await fetch(thumbUrl, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'image/webp' },
    body: blob,
  });
  if (!resp.ok) throw new Error(`thumb upload failed (${resp.status})`);
  const j = await resp.json().catch(() => ({} as any));
  const thumbKey = j?.thumbKey ? String(j.thumbKey) : null;
  if (!thumbKey) throw new Error('thumb upload missing thumbKey');
  return thumbKey;
};

const makeThumbUrl = (fileId: string, thumbKey: string) =>
  `/api/files/thumb?fileId=${encodeURIComponent(fileId)}&v=${encodeURIComponent(thumbKey)}`;

export const generatePdfThumb = async (file: any) => {
  const url = `/api/files/download?fileId=${encodeURIComponent(String(file.id))}`;
  // @ts-ignore
  const task = pdfjs.getDocument({ url, withCredentials: true });
  const doc = await task.promise;
  const page = await doc.getPage(1);

  const baseViewport = page.getViewport({ scale: 1 });
  const targetW = 420;
  const scale = Math.max(0.2, Math.min(2.0, targetW / (baseViewport.width || targetW)));
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement('canvas');
  const fullWidth = Math.max(1, Math.round(viewport.width));
  const fullHeight = Math.max(1, Math.round(viewport.height));
  const thumbHeight = Math.round((fullWidth * 10) / 16);

  canvas.width = fullWidth;
  canvas.height = thumbHeight;
  const ctx2d = canvas.getContext('2d');
  if (!ctx2d) throw new Error('canvas context missing');

  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = fullWidth;
  tempCanvas.height = fullHeight;
  const tempCtx = tempCanvas.getContext('2d');
  if (!tempCtx) throw new Error('temp canvas context missing');

  await page.render({ canvasContext: tempCtx as any, viewport } as any).promise;
  ctx2d.drawImage(tempCanvas, 0, 0, fullWidth, thumbHeight, 0, 0, fullWidth, thumbHeight);

  const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', 0.82));
  if (!blob) throw new Error('thumb blob failed');

  const thumbKey = await uploadThumbBlob(String(file.id), blob);
  return makeThumbUrl(String(file.id), thumbKey);
};

export const generateVideoThumb = async (file: any) => {
  const ext = String(file.name || '').split('.').pop()?.toLowerCase() || '';
  const MIME_MAP: Record<string, string> = {
    mp4: 'video/mp4', webm: 'video/webm', ogv: 'video/ogg',
    mov: 'video/quicktime', avi: 'video/x-msvideo', mkv: 'video/x-matroska',
    m4v: 'video/mp4', '3gp': 'video/3gpp',
  };
  const mime = file.mime || MIME_MAP[ext] || 'video/mp4';

  // canPlayType('video/quicktime') returns '' on Chrome even though Chrome CAN play
  // H.264 .mov files. Only hard-reject formats that are definitively unsupported
  // by checking both the exact MIME and common fallback containers.
  const probe = document.createElement('video');
  const MOV_LIKE = new Set(['mov', 'm4v']);
  if (probe.canPlayType(mime) === '' && !MOV_LIKE.has(ext)) {
    throw new Error(`browser cannot decode ${mime} (${ext})`);
  }

  // Stream directly — do NOT download the full blob (could be GB-scale).
  // preload=metadata tells the browser to only fetch the moov/metadata box.
  const url = `/api/files/download?fileId=${encodeURIComponent(String(file.id))}`;

  const video = document.createElement('video');
  video.muted = true;
  // @ts-ignore
  video.playsInline = true;
  video.preload = 'metadata';
  video.src = url;  // same-origin: cookies sent automatically

  // Wait for metadata only (much faster than loadeddata for large files)
  await new Promise<void>((resolve, reject) => {
    video.addEventListener('loadedmetadata', () => resolve(), { once: true });
    video.addEventListener('error', () => reject(new Error('video load failed')), { once: true });
    setTimeout(() => reject(new Error('video metadata timeout')), 30000);
  });

  // Seek 5% into the video (or 1s, whichever is smaller) for a representative frame
  const seekTo = Math.min(1, Math.max(0, (video.duration || 2) * 0.05));
  video.currentTime = seekTo;

  await new Promise<void>((resolve) => {
    video.addEventListener('seeked', () => resolve(), { once: true });
    setTimeout(() => resolve(), 3000);
  });

  const srcW = video.videoWidth || 1280;
  const srcH = video.videoHeight || 720;
  const outW = 420;
  const outH = Math.round((outW * 10) / 16);

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx2d = canvas.getContext('2d');
  if (!ctx2d) throw new Error('canvas context missing');

  const targetAspect = outW / outH;
  const sourceAspect = srcW / srcH;
  let sx = 0, sy = 0, sw = srcW, sh = srcH;
  if (sourceAspect > targetAspect) {
    sw = Math.round(srcH * targetAspect);
    sx = Math.round((srcW - sw) / 2);
  } else {
    sh = Math.round(srcW / targetAspect);
    sy = 0;
  }
  ctx2d.drawImage(video, sx, sy, sw, sh, 0, 0, outW, outH);
  video.src = '';  // release the stream

  const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', 0.82));
  if (!blob) throw new Error('thumb blob failed');

  const thumbKey = await uploadThumbBlob(String(file.id), blob);
  return makeThumbUrl(String(file.id), thumbKey);
};

export const generateTextThumb = async (file: any) => {
  const url = `/api/files/download?fileId=${encodeURIComponent(String(file.id))}`;
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) throw new Error('text fetch failed');
  const text = await response.text();

  const outW = 420;
  const outH = Math.round((outW * 10) / 16);
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx2d = canvas.getContext('2d');
  if (!ctx2d) throw new Error('canvas context missing');

  ctx2d.fillStyle = '#ffffff';
  ctx2d.fillRect(0, 0, outW, outH);
  ctx2d.fillStyle = '#334155';
  ctx2d.font = '12px ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace';
  const lines = text.split('\n').slice(0, 20);
  const lineHeight = 15;
  const padding = 16;
  lines.forEach((line, i) => {
    const y = padding + (i + 1) * lineHeight;
    if (y < outH - padding) ctx2d.fillText(line.slice(0, 60), padding, y);
  });
  ctx2d.strokeStyle = '#e2e8f0';
  ctx2d.lineWidth = 2;
  ctx2d.strokeRect(0, 0, outW, outH);

  const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', 0.82));
  if (!blob) throw new Error('thumb blob failed');

  const thumbKey = await uploadThumbBlob(String(file.id), blob);
  return makeThumbUrl(String(file.id), thumbKey);
};

export const generateOfficeThumb = async (file: any) => {
  const outW = 420;
  const outH = Math.round((outW * 10) / 16);
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx2d = canvas.getContext('2d');
  if (!ctx2d) throw new Error('canvas context missing');

  const colors: Record<string, { bg: string; accent: string; icon: string }> = {
    doc: { bg: '#EFF6FF', accent: '#3B82F6', icon: 'W' },
    ppt: { bg: '#FFF7ED', accent: '#F97316', icon: 'P' },
    sheet: { bg: '#F0FDF4', accent: '#22C55E', icon: 'X' },
  };
  const c = colors[file.type] || { bg: '#F8FAFC', accent: '#64748B', icon: '?' };

  ctx2d.fillStyle = c.bg;
  ctx2d.fillRect(0, 0, outW, outH);
  const cx = outW / 2;
  const cy = outH / 2 - 10;
  ctx2d.beginPath();
  ctx2d.arc(cx, cy, 40, 0, Math.PI * 2);
  ctx2d.fillStyle = c.accent;
  ctx2d.fill();
  ctx2d.fillStyle = '#ffffff';
  ctx2d.font = 'bold 36px system-ui, -apple-system, sans-serif';
  ctx2d.textAlign = 'center';
  ctx2d.textBaseline = 'middle';
  ctx2d.fillText(c.icon, cx, cy);
  ctx2d.fillStyle = '#475569';
  ctx2d.font = 'bold 13px system-ui, -apple-system, sans-serif';
  ctx2d.textAlign = 'center';
  const displayName = file.name.length > 35 ? file.name.slice(0, 32) + '...' : file.name;
  ctx2d.fillText(displayName, cx, cy + 60);
  ctx2d.strokeStyle = '#e2e8f0';
  ctx2d.lineWidth = 2;
  ctx2d.strokeRect(0, 0, outW, outH);

  const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', 0.82));
  if (!blob) throw new Error('thumb blob failed');

  const thumbKey = await uploadThumbBlob(String(file.id), blob);
  return makeThumbUrl(String(file.id), thumbKey);
};

export const generateImageThumb = async (file: any) => {
  // Skip very large images — downloading >8 MB just to make a thumbnail is too slow
  const sizeBytes = Number(file.sizeBytes ?? file.size_bytes ?? 0);
  if (sizeBytes > 8 * 1024 * 1024) throw new Error('image too large for browser thumbnail');

  // Load via <img> element (uses browser cache, no manual fetch+blob allocation)
  const url = `/api/files/download?fileId=${encodeURIComponent(String(file.id))}&inline=1`;
  const img = new Image();
  // Same-origin request with credentials (cookies) — no CORS taint issue
  img.src = url;
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('image load failed'));
    setTimeout(() => reject(new Error('image load timeout')), 20000);
  });

  const outW = 420;
  const outH = Math.round((outW * 10) / 16);
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx2d = canvas.getContext('2d');
  if (!ctx2d) throw new Error('canvas context missing');

  // Cover-crop: fill 420×262 centred
  const srcW = img.naturalWidth || outW;
  const srcH = img.naturalHeight || outH;
  const srcAspect = srcW / srcH;
  const dstAspect = outW / outH;
  let sx = 0, sy = 0, sw = srcW, sh = srcH;
  if (srcAspect > dstAspect) {
    sw = Math.round(srcH * dstAspect);
    sx = Math.round((srcW - sw) / 2);
  } else {
    sh = Math.round(srcW / dstAspect);
  }
  ctx2d.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);

  const thumbBlob: Blob | null = await new Promise((resolve) =>
    canvas.toBlob(resolve, 'image/webp', 0.82)
  );
  if (!thumbBlob) throw new Error('thumb blob failed');

  const thumbKey = await uploadThumbBlob(String(file.id), thumbBlob);
  return makeThumbUrl(String(file.id), thumbKey);
};

/**
 * Generate a 1600px-wide WebP preview thumbnail for an image file and upload
 * it to `thumbs/{fileId}/preview.webp` via `?size=preview`.
 * Fire-and-forget: returns void, silently ignores errors.
 */
export const generatePreviewThumb = async (
  fileId: string,
  imgSrc: string, // object URL or download URL
): Promise<void> => {
  try {
    const img = new Image();
    img.src = imgSrc;
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('preview thumb img load failed'));
      setTimeout(() => reject(new Error('preview thumb timeout')), 30000);
    });

    const MAX_W = 1600;
    const srcW = img.naturalWidth || MAX_W;
    const srcH = img.naturalHeight || MAX_W;
    const scale = Math.min(1, MAX_W / srcW);
    const outW = Math.max(1, Math.round(srcW * scale));
    const outH = Math.max(1, Math.round(srcH * scale));

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(img, 0, 0, outW, outH);

    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/webp', 0.9)
    );
    if (!blob) return;

    await fetch(`/api/uploads/thumb/upload?fileId=${encodeURIComponent(fileId)}&size=preview`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'image/webp' },
      body: blob,
    });
  } catch {
    // best-effort — never surface errors to caller
  }
};

// All file types for which thumbnails can be generated / backfilled
export const THUMB_SUPPORTED_TYPES = new Set(['image', 'pdf', 'video', 'code', 'doc', 'ppt', 'sheet']);

// Session-level blacklist — file IDs that permanently failed (e.g. unsupported video codec).
// Kept in inFlightSet permanently so the backfill filter skips them forever.
// Exported so callers can seed it after restoring persisted state if needed.
export const thumbPermFailed = new Set<string>();

export const maybeGenerateThumb = async (
  file: any,
  inFlightSet: Set<string>
): Promise<string | null> => {
  const id = String(file?.id || '');
  if (!id) return null;
  if (inFlightSet.has(id)) return null;     // in-flight or permanently failed
  if (thumbPermFailed.has(id)) return null; // already blacklisted this session
  if (file.thumb) return null;
  if (file.shared) return null;
  if (!THUMB_SUPPORTED_TYPES.has(file.type)) return null;

  inFlightSet.add(id);
  try {
    if (file.type === 'image') return await generateImageThumb(file);
    if (file.type === 'pdf') return await generatePdfThumb(file);
    if (file.type === 'video') return await generateVideoThumb(file);
    if (file.type === 'code') return await generateTextThumb(file);
    if (file.type === 'doc' || file.type === 'ppt' || file.type === 'sheet') return await generateOfficeThumb(file);
    return null;
  } catch (e) {
    console.warn('thumb gen failed', file.name, e);
    // Permanently blacklist ALL types that fail — 404 (missing R2 content),
    // unsupported codecs, canvas errors, etc. are not transient; don't retry.
    thumbPermFailed.add(id);
    return null;
  } finally {
    // Only remove from inFlight if NOT permanently failed — perm-failed stay in forever
    if (!thumbPermFailed.has(id)) {
      inFlightSet.delete(id);
    }
  }
};
